import { createAsyncThunk } from "@reduxjs/toolkit";
import Api from "../../Config/ApiConfig";
import { toast } from "react-toastify";

export const fetchSupportCategories = createAsyncThunk("supportKb/fetchCategories", async () => {
  try {
    const res = await Api.get("knowledge-base/categories");
    if (res.data?.status_code === 200) {
      return res.data.data;
    }
    return [];
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to load categories");
    throw err;
  }
});

export const fetchSupportTypes = createAsyncThunk("supportKb/fetchTypes", async () => {
  try {
    const res = await Api.get("knowledge-base/types");
    if (res.data?.status_code === 200) {
      return res.data.data;
    }
    return [];
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to load types");
    throw err;
  }
});

export const fetchSupportKbItems = createAsyncThunk("supportKb/fetchItems", async (params) => {
  try {
    const res = await Api.get("knowledge-base", { params });
    if (res.data?.status_code === 200) {
      return res.data;
    }
    return { data: [], totalItems: 0 };
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to load support knowledge items");
    throw err;
  }
});

export const searchSupportKbItems = createAsyncThunk("supportKb/searchItems", async ({ q, category, type, audience }) => {
  try {
    const res = await Api.get("knowledge-base/search", { params: { q, category, type, audience } });
    if (res.data?.status_code === 200) {
      const data = res.data.data;
      return Array.isArray(data) ? data : (data?.items || []);
    }
    return [];
  } catch (err) {
    toast.error(err?.response?.data?.message || "Support knowledge search failed");
    throw err;
  }
});

export const getSupportKbItemById = createAsyncThunk("supportKb/getById", async (id) => {
  try {
    const res = await Api.get(`knowledge-base/${id}`);
    if (res.data?.status_code === 200) {
      return res.data.data;
    }
    throw new Error(res.data?.message || "Failed to fetch item details");
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to fetch item details");
    throw err;
  }
});

export const createSupportKbItem = createAsyncThunk("supportKb/create", async (payload) => {
  try {
    const res = await Api.post("knowledge-base", payload);
    if (res.data?.status_code === 201 || res.data?.status_code === 200) {
      toast.success("Support knowledge item created successfully");
      return res.data.data;
    }
    throw new Error(res.data?.message || "Failed to create knowledge item");
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to create knowledge item");
    throw err;
  }
});

export const bulkCreateSupportKbItems = createAsyncThunk("supportKb/bulkCreate", async (items) => {
  try {
    const res = await Api.post("knowledge-base/bulk", { items });
    if (res.data?.status_code === 200 || res.data?.status_code === 201) {
      toast.success(`Successfully imported ${res.data.data?.length || items.length} support knowledge items`);
      return res.data.data;
    }
    throw new Error(res.data?.message || "Failed to bulk import knowledge items");
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to bulk import knowledge items");
    throw err;
  }
});

export const updateSupportKbItem = createAsyncThunk("supportKb/update", async ({ id, data }) => {
  try {
    const res = await Api.patch(`knowledge-base/${id}`, data);
    if (res.data?.status_code === 200) {
      toast.success("Support knowledge item updated successfully");
      return res.data.data;
    }
    throw new Error(res.data?.message || "Failed to update knowledge item");
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to update knowledge item");
    throw err;
  }
});

export const activateSupportKbItem = createAsyncThunk("supportKb/activate", async (id) => {
  try {
    const res = await Api.patch(`knowledge-base/${id}/activate`);
    if (res.data?.status_code === 200) {
      toast.success("Support knowledge item activated");
      return res.data.data;
    }
    throw new Error(res.data?.message || "Failed to activate item");
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to activate item");
    throw err;
  }
});

export const deactivateSupportKbItem = createAsyncThunk("supportKb/deactivate", async (id) => {
  try {
    const res = await Api.patch(`knowledge-base/${id}/deactivate`);
    if (res.data?.status_code === 200) {
      toast.success("Support knowledge item deactivated");
      return res.data.data;
    }
    throw new Error(res.data?.message || "Failed to deactivate item");
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to deactivate item");
    throw err;
  }
});

export const deleteSupportKbItem = createAsyncThunk("supportKb/delete", async ({ id, permanent }) => {
  try {
    const res = await Api.delete(`knowledge-base/${id}`, { params: { permanent } });
    if (res.data?.status_code === 200) {
      toast.success(permanent ? "Support knowledge item permanently deleted" : "Support knowledge item deactivated");
      return { id, permanent };
    }
    throw new Error(res.data?.message || "Failed to delete knowledge item");
  } catch (err) {
    toast.error(err?.response?.data?.message || "Failed to delete knowledge item");
    throw err;
  }
});
